/**
 * Godot's ambient occlusion in three's lit programs. three's `aoMap` reads the red channel and
 * occludes indirect light only. Godot reads the channel `ao_texture_channel` names
 * (`material.cpp:1952`), and also scales the direct light by `mix(1.0, ao, ao_light_affect)`
 * (`scene_forward_clustered.glsl:2213,2944-2945`).
 */
import { BASE_MATERIAL_3D_DEFAULTS } from '../../godot/scene/baseMaterial3D';
import { recordedUniform } from './recordedOnMaterial';
import { editedChunk, editedProgram, installChunkEdits } from '../shaderPatch/chunkPatch';
import { DEFAULT_TEXTURE_CHANNEL_MASK, } from '../../godot/rendering/materials/textureChannel';
const AO_PARS_CHUNK = '#include <aomap_pars_fragment>';
const AO_CHUNK = '#include <aomap_fragment>';
/**
 * The global that holds the mix of the surface's decals into its occlusion: `ao` becomes
 * `ao * x + y`, which composes every ORM mix of the decals (`scene_forward_clustered.glsl:1634`)
 * whatever `ao` the surface started from. The decals write it, and it reads `(1, 0)` otherwise.
 */
export const OCCLUSION_MIX = 'godotOcclusionMix';
const THREE_AO_GATE = '#ifdef USE_AOMAP';
const THREE_AO_SAMPLE = 'float ambientOcclusion = ( texture2D( aoMap, vAoMapUv ).r - 1.0 ) * aoMapIntensity + 1.0;';
/**
 * The global that holds Godot's `ao_light_affect`: the share of the occlusion the direct light
 * takes. 0 where the material sets none (`:1249`).
 */
export const LIGHT_AFFECT = 'godotLightAffect';
const MATERIAL_LIGHT_AFFECT = `float ${LIGHT_AFFECT} = 0.0;`;
/** The last statement that writes the surface's own `ao`, after which the screen-space one folds in. */
export const SURFACE_OCCLUSION = `ambientOcclusion = ambientOcclusion * ${OCCLUSION_MIX}.x + ${OCCLUSION_MIX}.y;`;
/** Godot's `ao` before the surface's own terms read it: 1 where there is no AO map (`:1248`). */
const GODOT_AO_SAMPLE = `float ambientOcclusion = 1.0;
	${MATERIAL_LIGHT_AFFECT}
	#ifdef USE_AOMAP
		ambientOcclusion = ( texture2D( aoMap, vAoMapUv ).r - 1.0 ) * aoMapIntensity + 1.0;
	#endif
	${SURFACE_OCCLUSION}`;
/**
 * Godot's occlusion of the direct light (`:2213`, `:2944-2945`). Sheen carries Godot's rim, which
 * Godot adds to the diffuse light, and clearcoat goes to the specular light
 * (`scene_forward_lights_inc.glsl:163,185`), so both take it too. At an affect of 0 the factor is
 * exactly 1.
 */
const DIRECT_OCCLUSION = /* glsl */ `
	float godotDirectOcclusion = mix( 1.0, ambientOcclusion, ${LIGHT_AFFECT} );
	reflectedLight.directDiffuse *= godotDirectOcclusion;
	reflectedLight.directSpecular *= godotDirectOcclusion;
	#ifdef USE_CLEARCOAT
		clearcoatSpecularDirect *= godotDirectOcclusion;
	#endif
	#ifdef USE_SHEEN
		sheenSpecularDirect *= godotDirectOcclusion;
	#endif
`;
/**
 * Makes `ambientOcclusion` exist for every surface, with the decals' ORM mix in it, as Godot's `ao`
 * does, and occludes the direct light by its `ao_light_affect`. three scales the indirect light by
 * it only for a surface with an AO map. At 1 each statement of the chunk multiplies by exactly 1,
 * so a surface with no AO map and no decal draws the same bits. True when the chunks hold the edit.
 * A second call changes nothing.
 */
export function installGodotOcclusion() {
    return installChunkEdits({
        edits: [
            {
                chunk: 'aomap_pars_fragment',
                three: THREE_AO_GATE,
                godot: `vec2 ${OCCLUSION_MIX} = vec2( 1.0, 0.0 );\n${THREE_AO_GATE}`,
            },
            { chunk: 'aomap_fragment', three: THREE_AO_GATE, godot: '#if 1' },
            { chunk: 'aomap_fragment', three: THREE_AO_SAMPLE, godot: GODOT_AO_SAMPLE },
        ],
        append: { aomap_fragment: DIRECT_OCCLUSION },
        mark: OCCLUSION_MIX,
        patch: 'occlusion',
    });
}
const MASKED_SAMPLE = {
    three: 'texture2D( aoMap, vAoMapUv ).r',
    godot: 'dot( texture2D( aoMap, vAoMapUv ), godotAoTextureChannel )',
};
/** The material's own `ao_light_affect`, in place of none. */
const MATERIAL_LIGHT_AFFECT_UNIFORM = {
    three: MATERIAL_LIGHT_AFFECT,
    godot: `float ${LIGHT_AFFECT} = godotAoLightAffect;`,
};
/**
 * three's AO chunk inlined, as `onBeforeCompile` sees only the `#include`, with the channel mask
 * in place of the red sample. Read at each compile, so it holds the chunk as the shader patches
 * leave it. The light affect is the occlusion patch's, so it takes the material's own only where
 * the patch went in: a three release the patch fails on keeps the masked sample.
 */
function godotAoFragment() {
    if (!installGodotOcclusion())
        return editedChunk('aomap_fragment', MASKED_SAMPLE);
    return editedChunk('aomap_fragment', MASKED_SAMPLE, MATERIAL_LIGHT_AFFECT_UNIFORM);
}
const GODOT_AO_PARS = `${AO_PARS_CHUNK}
uniform float godotAoLightAffect;
uniform vec4 godotAoTextureChannel;`;
/** Where a material records its `ao_light_affect`. */
const LIGHT_AFFECT_KEY = 'godotAoLightAffect';
/** Where a material records its `ao_texture_channel` mask. */
const CHANNEL_MASK_KEY = 'godotAoTextureChannel';
/** The `userData` that gives a material its `ao_light_affect` and `ao_texture_channel`. */
export function ambientOcclusionUserData(lightAffect, channelMask) {
    return { [LIGHT_AFFECT_KEY]: lightAffect, [CHANNEL_MASK_KEY]: channelMask };
}
function ambientOcclusionUniforms(material) {
    return {
        godotAoLightAffect: recordedUniform(material, LIGHT_AFFECT_KEY, BASE_MATERIAL_3D_DEFAULTS.aoLightAffect),
        godotAoTextureChannel: recordedUniform(material, CHANNEL_MASK_KEY, DEFAULT_TEXTURE_CHANNEL_MASK),
    };
}
/** What a lit material with a bound AO map, or with an `ao_light_affect` above 0, compiles with. */
export const GODOT_AMBIENT_OCCLUSION = {
    cacheKey: 'godot-ambient-occlusion',
    onBeforeCompile(shader) {
        Object.assign(shader.uniforms, ambientOcclusionUniforms(this));
        editedProgram(shader, {
            fragment: [
                { three: AO_PARS_CHUNK, godot: GODOT_AO_PARS },
                { three: AO_CHUNK, godot: godotAoFragment() },
            ],
        }, 'ambient occlusion');
    },
};
//# sourceMappingURL=godotAmbientOcclusion.js.map